import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Interface for Data Lakes
interface DataLake {
    id: string;
    lakeName: string; // The name of the Dataplex Lake
    projectId: string;
    location: string;
    status: 'pending' | 'active' | 'rejected' | string;
    solutionCategoryType: string;
    cloudProvider: string;
    updatedAt?: string;
    createdAt?: string;
    solutionOwner?: SolutionOwnerInfo | null;
    reasonForRejection?: string;
}

interface DataLakesTableProps { // Changed interface name
    dataLakes: DataLake[]; // Changed prop name
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected';
}

const DataLakesTable: React.FC<DataLakesTableProps> = ({ // Changed component name
    dataLakes, // Changed prop name
    columnFilters,
    statusType,
}) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    
    const handleRowClick = (dataLakeId: string) => { // Changed variable name
        navigate(`/datasolutions/data-lakes/details/${dataLakeId}`); // Changed navigation path
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
            if (!dateValue) return null;
            const numericDate = Number(dateValue);
            if (isNaN(numericDate)) return null;
            return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
        };

    // Updated headers for Data Lakes table
    const headers = [
        { key: "lakeName", label: "DATA LAKE NAME", xs: 2.5 }, // Changed from dataSourceName
        { key: "projectId", label: "PROJECT ID", xs: 2 },
        { key: "cloudProvider", label: "CLOUD IDENTIFIER", xs: 2 },
        { key: "location", label: "LOCATION", xs: 1.5 }, // Added location
        { key: "solutionOwner", label: "RESOURCE OWNER", xs: 1.5 },
        { key: "createdAt", label: "ADDED ON", xs: 1.5 },
    ];

    const iconColor = statusType === "active" ? "#12B76A" : statusType === "rejected" ? "#F04438" : "#FDB022";
    const listItemBgColor = statusType === "active" ? "#FFFFFF" : statusType === "rejected" ? "#F0443810" : "#FDB02210";


    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            {/* Table Header */}
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {/* Table Body - DataLake Rows */}
            {dataLakes.map((dataLake, index) => ( // Changed variable name
                <ListItem
                    key={dataLake.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        width: '100%',
                        backgroundColor: listItemBgColor,
                        '&:hover': {
                            borderColor: "black",
                        }
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                    onClick={() => handleRowClick(dataLake.id)} // Changed variable name
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.lakeName && ( // Changed key
                                <Grid item xs={headers.find(h => h.key === 'lakeName')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon
                                            sx={{
                                                color: iconColor,
                                                bgcolor: iconColor,
                                                width: "4px",
                                                height: "22px",
                                                marginRight: "8px",
                                            }}
                                        />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {dataLake.lakeName || "N/A"} {/* Changed field */}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.projectId && (
                                <Grid item xs={headers.find(h => h.key === 'projectId')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {dataLake.projectId || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" sx={{textTransform: 'uppercase'}}>
                                        {dataLake.cloudProvider || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                             {columnFilters.location && ( // Added location column
                                <Grid item xs={headers.find(h => h.key === 'location')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dataLake.location || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.solutionOwner && (
                                <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                            src={dataLake.solutionOwner?.googleProfilePicture || undefined}
                                            alt={dataLake.solutionOwner?.name || ""}
                                            sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: dataLake.solutionOwner?.googleProfilePicture ? 'transparent' : 'rgba(0,0,0,0.1)' }}
                                        >
                                            {dataLake.solutionOwner && !dataLake.solutionOwner.googleProfilePicture ? dataLake.solutionOwner.name.charAt(0).toUpperCase() : null}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {dataLake.solutionOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(Number(dataLake.createdAt))}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default DataLakesTable;